import React, { useCallback } from 'react';
import cn from 'classnames';

import { useGetMessage } from 'common/translate';
import formatter from 'common/helpers/format/formatter';

import ExternalLink from 'components/ExternalLink';

import LineItemContext from './context';
import { Line, LocationLine } from './nested/Line';
import styles from './line-items-mixed-pricing-catalogue.module.css';

const LineItemsMixedPricingCatalogue = ({ categories, isLocation, disabledState = false, lockedState = false }) => {
  const getMessage = useGetMessage();

  const getThead = useCallback(
    category =>
      isLocation ? (
        <thead>
          <tr>
            <th colSpan="6">{getMessage('msg_one_time')}</th>
            <th colSpan="3">{getMessage('msg_recurring')}</th>
            <th colSpan="1"></th>
          </tr>
          <tr>
            <th className={cn(styles.name, { [styles.hasError]: category.hasError })}>{category.name}</th>
            <th>{getMessage('msg_total_quantity')}</th>
            <th>{getMessage('msg_available')}</th>
            <th className={styles.quantity}>{getMessage('msg_quantity')}</th>
            <th>{getMessage('msg_adjusted')}</th>
            <th>{getMessage('msg_extended')}</th>
            <th className={styles.priceModel}></th>
            <th>{getMessage('msg_adjusted')}</th>
            <th>{getMessage('msg_extended')}</th>
          </tr>
        </thead>
      ) : (
        <thead>
          <tr>
            <th colSpan="2"></th>
            <th colSpan="6" className={styles.header}>
              {getMessage('msg_one_time')}
            </th>
            <th colSpan="5" className={styles.header}>
              {getMessage('msg_recurring')}
            </th>
          </tr>
          <tr>
            <th className={cn(styles.name, { [styles.hasError]: category.hasError })}>{category.name}</th>
            <th className={styles.quantity}>{getMessage('msg_quantity')}</th>
            <th className={styles.msrp}>{getMessage('msg_msrp')}</th>
            <th>{getMessage('msg_discount')}</th>
            <th>(%)</th>
            <th>{getMessage('msg_adjusted')}</th>
            <th>{getMessage('msg_extended')}</th>
            <th className={styles.priceModel}></th>
            <th className={styles.msrp}>{getMessage('msg_msrp')}</th>
            <th>{getMessage('msg_discount')}</th>
            <th>(%)</th>
            <th>{getMessage('msg_adjusted')}</th>
            <th>{getMessage('msg_extended')}</th>
            <th />
          </tr>
        </thead>
      ),
    [isLocation, getMessage]
  );

  return (
    <div className={styles.root}>
      <table>
        {categories
          .filter(({ active, hidden }) => active && !hidden)
          .map((category, index) => {
            const lineItems = category.visibleLineItems || [];
            const { hideAddButton, addButton } = category;
            return (
              <React.Fragment key={`mixed-line-items-${category.uuid}`}>
                {!!index && (
                  <tr className={styles.divider}>
                    <td colSpan="13">
                      <hr />
                    </td>
                  </tr>
                )}
                {getThead(category)}
                <tbody>
                  {!lineItems.length ? (
                    <tr>
                      <td colSpan={13} className={styles.noItems}>
                        {getMessage('msg_no_line_items_have_been_added')}
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((lineItem, index) => (
                      <LineItemContext.Provider key={lineItem.uuid} value={lineItem}>
                        {isLocation ? (
                          <LocationLine index={index} disabledState={disabledState} lockedState={lockedState} />
                        ) : (
                          <Line index={index} disabledState={disabledState} lockedState={lockedState} />
                        )}
                      </LineItemContext.Provider>
                    ))
                  )}
                  <tr className={styles.totals}>
                    <td colSpan="7">
                      <span className={styles.total}>
                        {formatter(
                          lineItems.reduce((sum, lineItem) => sum + lineItem.upfrontTotal, 0),
                          2
                        )}
                      </span>
                    </td>
                    <td colSpan="6">
                      <span className={styles.total}>
                        {formatter(
                          lineItems.reduce((sum, lineItem) => sum + lineItem.recurringTotal, 0),
                          2
                        )}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <tr className={styles.footer}>
                  <td colSpan="13">
                    <span className={styles.gridWrapper}>
                      <span>
                        {addButton.visible && !hideAddButton && (
                          <button
                            type="button"
                            id={addButton.id}
                            onClick={addButton.handleClick}
                            disabled={addButton.disabled}
                            className="large outlined-primary"
                          >
                            <span className="icon-add" />
                            <span>{getMessage('msg_add')}</span>
                          </button>
                        )}
                      </span>

                      <span className={styles.notes}>
                        {!!category.hintLinkUrl && (
                          <ExternalLink path={category.hintLinkUrl} title={category.hintLinkText} />
                        )}
                        <br />
                        <span>{category.subHintText}</span>
                      </span>
                    </span>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
      </table>
    </div>
  );
};

export default LineItemsMixedPricingCatalogue;
